// Event Handler - 事件处理器
// 处理各种随机事件的逻辑

import { gainXp } from '../realms';
import * as Skills from '../skills';
import { randomEvents } from './exploration-data.js';

class EventHandler {
  constructor(stateManager) {
    this.stateManager = stateManager;
  }

  // 处理事件效果
  processEventEffects(eventId, effects) {
    const messages = [];
    const state = this.stateManager.getState();

    for (const effect of effects) {
      switch (effect.type) {
        case 'xp':
          messages.push(...gainXp(state, effect.value));
          break;

        case 'skill':
          messages.push(...Skills.learn(state, effect.skillName, effect.level || 1));
          break;

        case 'item':
          const count = typeof effect.count === 'function' ? effect.count() : effect.count;
          this.stateManager.addItemToBag(effect.itemName, count);
          messages.push(`获得了 ${count} 个 ${effect.itemName}`);
          break;

        case 'attribute':
          if (state.attributes && state.attributes[effect.attribute] !== undefined) {
            state.attributes[effect.attribute] += effect.value;
            messages.push(`${effect.attribute} 增加了 ${effect.value}`);
          }
          break;

        case 'qi':
          state.qi = Math.max(0, state.qi + effect.value);
          this.stateManager.updateState({ qi: state.qi });
          messages.push(`气血 ${effect.value > 0 ? '恢复' : '减少'}了 ${Math.abs(effect.value)}`);
          break;

        case 'luck':
          if (!state.luck) state.luck = 0;
          state.luck += effect.value;
          this.stateManager.updateState({ luck: state.luck });
          messages.push(`幸运值增加了 ${effect.value}`);
          break;

        case 'mood':
          // Mood handling would need to be implemented in state
          messages.push(`心情变得 ${effect.mood}`);
          break;

        case 'wisdom':
          // Wisdom attribute
          messages.push(`智慧增加了 ${effect.value}`);
          break;

        case 'purify':
          // Purification effect
          messages.push(`身心得到了净化`);
          break;

        case 'inspiration':
          // Inspiration for creativity
          messages.push(`获得了 ${effect.value} 点灵感`);
          break;

        case 'mystery':
          // Mystery perception
          messages.push(`感知到了天地间的奥秘`);
          break;

        case 'treasure':
          // Treasure discovery
          messages.push(`发现了宝藏：${effect.treasureName}`);
          break;

        case 'buff':
          // Buff application
          messages.push(`获得了增益效果：${effect.buffName} (${effect.duration} 小时)`);
          break;

        case 'wish':
          // Wish granting
          messages.push(`获得了 ${effect.wishes} 个愿望`);
          break;

        case 'weather_change':
          // Weather manipulation
          messages.push(`天气变成了 ${effect.weather}`);
          break;

        case 'combat':
          // Combat encounter
          messages.push(`遭遇了 ${effect.enemy}，需要战斗！`);
          if (effect.reward) {
            effect.reward.forEach(item => {
              this.stateManager.addItemToBag(item, 1);
            });
            messages.push(`战斗胜利，获得了 ${effect.reward.join('、')}`);
          }
          break;

        case 'companion':
          // Companion acquisition
          messages.push(`获得了新伙伴：${effect.companion}`);
          break;

        case 'achievement':
          // Achievement unlock
          messages.push(`解锁成就：${effect.achievement}`);
          break;

        default:
          messages.push(`未知效果类型：${effect.type}`);
      }
    }

    return messages;
  }

  // 获取随机事件
  getRandomEvent(location) {
    // 首先检查是否有地点特定的权重
    const weights = {};
    const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);

    if (totalWeight > 0 && Math.random() < 0.7) { // 70% 概率触发地点特定事件
      const rand = Math.random() * totalWeight;
      let cumulative = 0;
      for (const [eventId, weight] of Object.entries(weights)) {
        cumulative += weight;
        if (rand <= cumulative) {
          return randomEvents.find(e => e.id === eventId);
        }
      }
    }

    // 否则随机选择全局事件
    const availableEvents = randomEvents.filter(event => Math.random() < event.probability);
    if (availableEvents.length > 0) {
      return availableEvents[Math.floor(Math.random() * availableEvents.length)];
    }

    return null;
  }

  // 执行随机事件
  executeRandomEvent(event, context = {}) {
    const messages = [];
    const state = this.stateManager.getState();

    // 添加事件描述
    messages.push(`【${event.name}】`);
    messages.push(event.description);
    messages.push('');
    messages.push(event.narrative);

    // 处理效果
    const effectMessages = this.processEventEffects(event.id, event.effects);
    messages.push(...effectMessages);

    // 记录事件
    if (!state.explorationEvents) {
      state.explorationEvents = [];
    }
    state.explorationEvents.push({
      id: event.id,
      name: event.name,
      timestamp: Date.now(),
      location: context.location || '未知',
      effects: event.effects
    });

    this.stateManager.updateState({ explorationEvents: state.explorationEvents });

    return messages;
  }

  // 检查彩蛋触发
  checkEasterEggs() {
    const messages = [];
    const state = this.stateManager.getState();

    // 这里可以实现彩蛋检查逻辑
    // 例如：连续探索次数、特定行为等

    return messages;
  }

  // 获取事件历史
  getEventHistory() {
    const state = this.stateManager.getState();
    if (!state.explorationEvents) {
      return ['还没有发生任何特殊事件。'];
    }

    const messages = ['=== 探索事件历史 ==='];
    state.explorationEvents.forEach((event, index) => {
      messages.push(`${index + 1}. ${event.name} (${new Date(event.timestamp).toLocaleString()})`);
      messages.push(`   地点：${event.location}`);
    });

    return messages;
  }
}

export default EventHandler;
